/**
 * The "Sign-out history" card and the "Last job" line, shared by the office
 * equipment detail page and the field equipment detail screen.
 *
 * Separate from the cert "Usage Log": that is days counted against a cert;
 * this is where the asset went and who moved it (api#985).
 *
 * Three states, never two: a failed load says so where the list would be; an
 * empty array is "No history yet."; anything else is the rows.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, History, Truck } from 'lucide-react';
import { api } from '../../api/client';
import { useCollapsibleGroups } from '../../hooks/useCollapsibleGroups';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import {
  describeHistoryRow,
  describeLastJob,
  parseEquipmentHistory,
  type HistoryRowView,
} from '../../lib/equipment-history';
import type { EquipmentLastJob } from '../../types/equipment-history';

export const HISTORY_PAGE_SIZE = 10;

type Variant = 'office' | 'field';

function jobHref(variant: Variant, jobId: string): string {
  return variant === 'field' ? `/field/jobs/${jobId}` : `/jobs/${jobId}`;
}

export function equipmentHistoryQueryKey(equipmentId: string) {
  return ['equipment-history', equipmentId] as const;
}

export function useEquipmentHistory(equipmentId: string | undefined) {
  return useQuery({
    queryKey: equipmentHistoryQueryKey(equipmentId ?? ''),
    queryFn: () => api.equipment.history(equipmentId!).then((r) => parseEquipmentHistory(r.data)),
    enabled: !!equipmentId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * The zone a last-job date is printed in: the one the history endpoint says
 * it used, else the tenant zone from the locale context. Never the browser's.
 */
function useHistoryZone(equipmentId: string | undefined): string | null {
  const tenantZone = useTenantTimeZone();
  const { data } = useEquipmentHistory(equipmentId);
  return data?.timeZone ?? tenantZone;
}

function HistoryRow({ view, variant }: { view: HistoryRowView; variant: Variant }) {
  const [open, setOpen] = useState(false);
  const expandable = view.changes.length > 0;
  const changesId = `history-changes-${view.id}`;
  return (
    <li className="py-2.5 border-b border-fieldiq-border last:border-b-0" data-testid="history-row">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
        <span className="text-xs tabular-nums text-fieldiq-muted whitespace-nowrap">{view.when}</span>
        {expandable ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={changesId}
            className="inline-flex items-center gap-1 font-semibold text-white text-left hover:underline"
          >
            {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
            {view.action}
          </button>
        ) : (
          <span className="font-semibold text-white">{view.action}</span>
        )}
        {view.jobId ? (
          <Link to={jobHref(variant, view.jobId)} className="font-medium text-fieldiq-accent-signal hover:underline">
            {view.jobNumber}
          </Link>
        ) : (
          <span className="text-fieldiq-muted">{view.jobNumber}</span>
        )}
        {view.fromJob && <span className="text-xs text-fieldiq-muted">{view.fromJob}</span>}
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-fieldiq-text">
        <span className="break-words">{view.rigWell}</span>
        <span className="text-fieldiq-muted">·</span>
        <span>{view.who}</span>
      </div>
      {view.note && <p className="mt-0.5 text-xs text-fieldiq-muted break-words">{view.note}</p>}
      {expandable && open && (
        <dl id={changesId} className="mt-2 space-y-1 rounded-lg bg-fieldiq-dark/60 px-3 py-2 text-xs">
          {view.changes.map((c, i) => (
            <div key={`${c.label}-${i}`} className="flex flex-wrap gap-x-2">
              <dt className="font-medium text-fieldiq-text">{c.label}</dt>
              <dd className="text-white break-words">
                {c.before} <span aria-label="changed to">→</span> {c.after}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}

/** Below Tailwind's `md`: the phone layout, where the card starts shut. */
function isNarrowViewport(): boolean {
  try {
    return window.matchMedia('(max-width: 767px)').matches;
  } catch {
    return false; // no matchMedia (jsdom, an old webview): the desktop default
  }
}

const HISTORY_GROUP = 'history';

export function EquipmentHistoryCard({ equipmentId, variant }: { equipmentId: string; variant: Variant }) {
  const query = useEquipmentHistory(equipmentId);
  const [shown, setShown] = useState(HISTORY_PAGE_SIZE);
  const failure = queryLoadFailure(query);
  const rows = query.data?.rows;

  // ── COLLAPSE: shut by default on a phone, open on a desktop ───────────────
  //
  // A phone opens this card scrolled past the Usage Log and the actions, and an
  // 18-row list there pushes Run Check off the screen; on a desktop it sits
  // beside room to spare. Decided once, at mount, from the viewport, so the
  // office page on a phone starts shut too.
  //
  // Remembered with useCollapsibleGroups in sessionStorage, NOT localStorage
  // (operator's call, 2026-10-09, per CLAUDE.md fe#680/fe#716): a toggle should
  // survive moving between assets in this tab, not outvote the default for
  // ever. One key per breakpoint, so a choice made on the desktop layout does
  // not decide the phone's default. Shared across assets: it is the viewer's
  // preference about the card, not about one asset.
  const [narrow] = useState(isNarrowViewport);
  const { isCollapsed, toggleGroup } = useCollapsibleGroups({
    defaultCollapsed: narrow,
    storageKey: `equipment:history:collapsed:${narrow ? 'narrow' : 'wide'}`,
  });
  const collapsed = isCollapsed(HISTORY_GROUP);
  const bodyId = `history-body-${equipmentId}`;

  // The count rides in the header in BOTH states (CLAUDE.md, fe#716): the size
  // is the reason to open it. Nothing while loading — never a placeholder 0 —
  // and an em dash when the load failed, since the size is then unknown.
  const countText = failure ? '\u2014' : rows ? String(rows.length) : null;
  const countWords = failure
    ? 'count unavailable'
    : rows ? `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}` : null;

  const cardClass = variant === 'field'
    ? 'bg-fieldiq-card rounded-xl p-4'
    : 'bg-fieldiq-card rounded-lg border border-fieldiq-border p-4';
  const headingClass = variant === 'field'
    ? 'text-white font-semibold text-sm'
    : 'text-base font-bold text-white';

  return (
    <section className={cardClass} aria-labelledby={`history-heading-${equipmentId}`} data-testid="equipment-history-card">
      <h3 id={`history-heading-${equipmentId}`} className={`${headingClass} ${collapsed ? '' : 'mb-2'}`}>
        <button
          type="button"
          onClick={() => toggleGroup(HISTORY_GROUP)}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          aria-label={countWords ? `Sign-out history, ${countWords}` : 'Sign-out history'}
          className="flex w-full items-center gap-2 text-left min-h-[36px]"
          data-testid="history-toggle"
        >
          {collapsed
            ? <ChevronRight size={16} className="text-fieldiq-muted flex-shrink-0" aria-hidden />
            : <ChevronDown size={16} className="text-fieldiq-muted flex-shrink-0" aria-hidden />}
          <History size={variant === 'field' ? 16 : 18} className="text-fieldiq-accent flex-shrink-0" aria-hidden />
          <span>Sign-out history</span>
          {countText && (
            <span className="font-normal text-fieldiq-muted tabular-nums" aria-hidden>({countText})</span>
          )}
        </button>
      </h3>
      <div id={bodyId} hidden={collapsed}>
      {collapsed ? null : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="sign-out history" />
      ) : !rows ? (
        <p className="text-sm text-fieldiq-muted py-3">Loading history…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-fieldiq-muted py-3">No history yet.</p>
      ) : (
        <>
          <ul aria-label="Sign-out history">
            {rows.slice(0, shown).map((row) => (
              <HistoryRow key={row.id} view={describeHistoryRow(row)} variant={variant} />
            ))}
          </ul>
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-xs text-fieldiq-muted">
              Showing {Math.min(shown, rows.length)} of {rows.length}
            </p>
            {shown < rows.length && (
              <button
                type="button"
                onClick={() => setShown((n) => n + HISTORY_PAGE_SIZE)}
                className="min-h-[36px] px-3 rounded-lg border border-fieldiq-border bg-fieldiq-card text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50"
              >
                Show more
              </button>
            )}
          </div>
        </>
      )}
      </div>
    </section>
  );
}

/**
 * "Last job: J-1234 · Rig 7 · well — Returned Oct 4, 2026 | Still out".
 * Renders nothing when the asset was never on a job.
 */
export function LastJobLine({
  equipmentId,
  lastJob,
  variant,
}: {
  equipmentId: string;
  lastJob: EquipmentLastJob | null;
  variant: Variant;
}) {
  const zone = useHistoryZone(equipmentId);
  const view = describeLastJob(lastJob, zone);
  if (!view) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 border-b border-fieldiq-border text-sm"
      data-testid="last-job-line"
    >
      <span className="flex items-center gap-1 text-fieldiq-text">
        <Truck size={14} aria-hidden />
        Last job:
      </span>
      <Link to={jobHref(variant, view.jobId)} className="font-medium text-white hover:underline break-words">
        {view.summary}
      </Link>
      <span className={view.stillOut ? 'font-semibold text-fieldiq-accent-signal' : 'text-fieldiq-muted'}>
        {view.state}
      </span>
    </div>
  );
}
